// Recipe list rendering for the workbench panel.
//
// Extracted from workbench-ui.ts to stay within the 400-line module cap.
// Builds the PixiJS subtree for the recipe list column: tier headers,
// recipe rows (locked/unlocked), and wires hover + select callbacks.
//
// onHover fires on pointer over/out — drives the info pane.
// onSelect fires on click of an unlocked row — drives the action bar.
// Clicking a selected row toggles it off. Clicking a locked row is a no-op.
// Selected rows keep a persistent highlight; hover temporarily overrides it.

import { Container, Graphics, Text } from "pixi.js";
import { recipeList, meetsSkillGates, type Recipe } from "../../shared/recipes.ts";
import { getItemDef } from "../../shared/items.ts";
import { balance } from "./types.ts";

const { lockedRecipeAlpha, lockedStrokeAlpha } = balance.workbench;

// -----------------------------------------------------------------------
// Style constants
// -----------------------------------------------------------------------

const PAD = 12; // contracts:allow-literal — inner padding px
const ROW_HEIGHT = 44; // contracts:allow-literal — px per recipe row
const ROW_CORNER = 3; // contracts:allow-literal — px corner radius
const ROW_BG = 0x1e1e38;
const ROW_HOVER = 0x2e2e58;
const ROW_SELECTED = 0x26265c; // persistent selection highlight
const ROW_LOCKED = 0x1e1818;
const SECTION_BG = 0x1a1a3a;
const SECTION_H = 22; // contracts:allow-literal — px
const ROW_GAP = 2; // contracts:allow-literal — px between rows
const SECTION_GAP = 6; // contracts:allow-literal — px between tier sections
const LOCK_COLOR = 0xcc6633;
const GATE_MET = 0x44cc44;
const GATE_UNMET = 0xff8844;
const TEXT_ACTIVE = 0xeeeeff;
const TEXT_LOCKED = 0x886666;
const TEXT_SUMMARY = 0xaaaacc;
const TEXT_SUMMARY_LOCKED = 0x665555;
const TEXT_SECTION = 0x9999cc;
const NAME_SIZE = 13; // contracts:allow-literal — px
const SUMMARY_SIZE = 11; // contracts:allow-literal — px

// -----------------------------------------------------------------------
// Internal selection state shared across all rows in one buildRecipeList call
// -----------------------------------------------------------------------

type RowEntry = { bg: Graphics; rowW: number; bgColor: number; alpha: number; unlocked: boolean };
type SelectionRef = { id: string | null; rows: Map<string, RowEntry> };

// -----------------------------------------------------------------------
// buildRecipeList
// -----------------------------------------------------------------------

export function buildRecipeList(
  parent: Container,
  x: number,
  y: number,
  width: number,
  skillXpMap: Record<string, number>,
  onHover: (recipe: Recipe | null, unlocked: boolean) => void,
  onSelect: (recipe: Recipe | null, unlocked: boolean) => void,
): void {
  const col = new Container();
  col.x = x;
  col.y = y;
  parent.addChild(col);

  const selRef: SelectionRef = { id: null, rows: new Map() };
  let cursor = PAD / 2; // contracts:allow-literal — small top pad

  const tier1 = recipeList.filter((r) => r.tier === 1); // contracts:allow-literal
  const tier2 = recipeList.filter((r) => r.tier === 2); // contracts:allow-literal

  cursor = buildTierSection(col, cursor, width, "Tier 1 — Basic", tier1, skillXpMap, onHover, onSelect, selRef);
  cursor += SECTION_GAP;
  buildTierSection(col, cursor, width, "Tier 2 — Advanced", tier2, skillXpMap, onHover, onSelect, selRef);
}

// -----------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------

function buildTierSection(
  parent: Container,
  startY: number,
  width: number,
  label: string,
  recipes: Recipe[],
  skillXpMap: Record<string, number>,
  onHover: (recipe: Recipe | null, unlocked: boolean) => void,
  onSelect: (recipe: Recipe | null, unlocked: boolean) => void,
  selRef: SelectionRef,
): number {
  let y = startY;

  const headerBg = new Graphics()
    .rect(PAD / 2, y, width - PAD, SECTION_H) // contracts:allow-literal — centering
    .fill({ color: SECTION_BG });
  parent.addChild(headerBg);

  const headerText = new Text({
    text: label,
    style: { fontSize: SUMMARY_SIZE, fill: TEXT_SECTION, fontFamily: "monospace", fontWeight: "bold" },
  });
  headerText.x = PAD;
  headerText.y = y + Math.round((SECTION_H - headerText.height) / 2); // contracts:allow-literal
  parent.addChild(headerText);
  y += SECTION_H + ROW_GAP;

  for (const recipe of recipes) {
    const unlocked = meetsSkillGates(recipe, skillXpMap);
    y = buildRecipeRow(parent, y, width, recipe, unlocked, skillXpMap, onHover, onSelect, selRef);
    y += ROW_GAP;
  }
  return y;
}

function buildRecipeRow(
  parent: Container,
  y: number,
  width: number,
  recipe: Recipe,
  unlocked: boolean,
  skillXpMap: Record<string, number>,
  onHover: (recipe: Recipe | null, unlocked: boolean) => void,
  onSelect: (recipe: Recipe | null, unlocked: boolean) => void,
  selRef: SelectionRef,
): number {
  const rowW = width - PAD;
  const alpha = unlocked ? 1.0 : lockedRecipeAlpha; // contracts:allow-literal
  const bgColor = unlocked ? ROW_BG : ROW_LOCKED;

  const container = new Container();
  container.x = PAD / 2; // contracts:allow-literal
  container.y = y;
  container.name = `recipe-${recipe.id}`;
  container.eventMode = "static";
  container.cursor = unlocked ? "pointer" : "default";
  parent.addChild(container);

  const rowBg = new Graphics();
  drawRowBg(rowBg, rowW, bgColor, alpha, unlocked);
  container.addChild(rowBg);

  // Register row in selection map for bg restore on selection changes.
  selRef.rows.set(recipe.id, { bg: rowBg, rowW, bgColor, alpha, unlocked });

  // Recipe name
  const nameText = new Text({
    text: recipe.name,
    style: {
      fontSize: NAME_SIZE,
      fill: unlocked ? TEXT_ACTIVE : TEXT_LOCKED,
      fontFamily: "monospace",
      fontWeight: "bold",
    },
  });
  nameText.x = PAD / 2; // contracts:allow-literal
  nameText.y = Math.round((ROW_HEIGHT / 2 - nameText.height) / 2) + 2; // contracts:allow-literal
  container.addChild(nameText);

  // Input → output summary
  const inputSummary = recipe.inputs.map((inp) => `${inp.count}× ${label(inp.itemId)}`).join(" + ");
  const secs = recipe.craftTimeMs / 1000; // contracts:allow-literal — ms to seconds
  const summaryText = new Text({
    text: `${inputSummary}  →  ${label(recipe.outputBaseId)}  (${secs}s)`,
    style: { fontSize: SUMMARY_SIZE, fill: unlocked ? TEXT_SUMMARY : TEXT_SUMMARY_LOCKED, fontFamily: "monospace" },
  });
  summaryText.x = PAD / 2; // contracts:allow-literal
  summaryText.y = nameText.y + nameText.height + 2; // contracts:allow-literal
  container.addChild(summaryText);

  // Lock badge for the first unmet skill gate
  if (!unlocked && recipe.skillGates.length > 0) {
    const firstUnmet = recipe.skillGates.find((g) => (skillXpMap[g.skillId] ?? 0) < g.minXp);
    if (firstUnmet) {
      const have = Math.floor(skillXpMap[firstUnmet.skillId] ?? 0);
      const lockText = new Text({
        text: `🔒 ${firstUnmet.skillId} ≥ ${firstUnmet.minXp} (have ${have})`,
        style: { fontSize: SUMMARY_SIZE, fill: GATE_UNMET, fontFamily: "monospace" },
      });
      lockText.x = rowW - lockText.width - PAD / 2; // contracts:allow-literal
      lockText.y = Math.round((ROW_HEIGHT - lockText.height) / 2); // contracts:allow-literal
      container.addChild(lockText);
    }
  }

  // Show met gate badge when all gates pass
  if (unlocked && recipe.skillGates.length > 0) {
    const allMet = recipe.skillGates.every((g) => (skillXpMap[g.skillId] ?? 0) >= g.minXp);
    if (allMet) {
      const badgeText = new Text({
        text: "✓ Unlocked",
        style: { fontSize: SUMMARY_SIZE, fill: GATE_MET, fontFamily: "monospace" },
      });
      badgeText.x = rowW - badgeText.width - PAD / 2; // contracts:allow-literal
      badgeText.y = Math.round((ROW_HEIGHT - badgeText.height) / 2); // contracts:allow-literal
      container.addChild(badgeText);
    }
  }

  // Restore this row's bg to its natural state (selected or normal), used by pointer handlers.
  const restoreBg = () => {
    rowBg.clear();
    const isSel = selRef.id === recipe.id;
    drawRowBg(rowBg, rowW, isSel ? ROW_SELECTED : bgColor, isSel ? 1.0 : alpha, !isSel && !unlocked); // contracts:allow-literal — 1.0 = fully opaque for selected rows
  };

  container.on("pointerover", () => {
    rowBg.clear();
    drawRowBg(rowBg, rowW, ROW_HOVER, alpha, false);
    onHover(recipe, unlocked);
  });
  container.on("pointerout", () => {
    restoreBg();
    onHover(null, false);
  });
  container.on("pointertap", () => {
    if (!unlocked) return;
    const prevId = selRef.id;

    // Deselect previously selected row (if different).
    if (prevId && prevId !== recipe.id) {
      const prev = selRef.rows.get(prevId);
      if (prev) {
        prev.bg.clear();
        drawRowBg(prev.bg, prev.rowW, prev.bgColor, prev.alpha, !prev.unlocked);
      }
    }

    if (prevId === recipe.id) {
      // Toggle off.
      selRef.id = null;
      restoreBg();
      onSelect(null, false);
    } else {
      selRef.id = recipe.id;
      rowBg.clear();
      drawRowBg(rowBg, rowW, ROW_SELECTED, 1.0, false); // contracts:allow-literal — 1.0 = fully opaque
      onSelect(recipe, unlocked);
    }
  });

  return y + ROW_HEIGHT;
}

function drawRowBg(g: Graphics, w: number, color: number, alpha: number, showLock: boolean): void {
  g.roundRect(0, 0, w, ROW_HEIGHT, ROW_CORNER).fill({ color, alpha });
  if (showLock) {
    g.stroke({ color: LOCK_COLOR, width: 1, alpha: lockedStrokeAlpha }); // contracts:allow-literal
  }
}

function label(itemId: string): string {
  return getItemDef(itemId)?.name ?? itemId;
}
